import posthog from "posthog-js";

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
const IS_DEV = import.meta.env.DEV;

let initialized = false;

export function initAnalytics() {
  if (IS_DEV || !POSTHOG_API_KEY || initialized) {
    return;
  }

  posthog.init(POSTHOG_API_KEY, {
    api_host: "https://us.i.posthog.com",
    autocapture: true,
    capture_pageview: false, // We handle SPA pageviews manually
    capture_pageleave: true,
    respect_dnt: true,
    persistence: "localStorage+cookie",
    disable_session_recording: true,
  });

  initialized = true;
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.identify(userId, traits);
}

export function resetUser() {
  if (!initialized) return;
  posthog.reset();
}

export function capturePageview() {
  if (!initialized) return;
  posthog.capture("$pageview");
}

// --- Typed event tracking ---

type AnalyticsEvent =
  | { event: "waitlist_viewed"; properties?: undefined }
  | { event: "waitlist_submit"; properties?: undefined }
  | { event: "waitlist_accepted"; properties: { durationMs: number } }
  | { event: "waitlist_failed"; properties: { reason: string } }
  | { event: "record_viewed"; properties: { masterId: string; title?: string } }
  | { event: "artist_viewed"; properties: { artistId: string; name?: string } }
  | { event: "store_viewed"; properties: { storeId: string; name?: string } }
  | { event: "search_performed"; properties: { query: string; resultCount?: number } }
  | { event: "filter_applied"; properties: { filterType: "genre" | "artist"; id: string; label: string } }
  | { event: "location_filtered"; properties: { country: string; region?: string; label: string } }
  | { event: "store_region_filtered"; properties: { state: string | null } }
  | { event: "record_saved"; properties: { masterId: string; listType: "want" | "collection" } }
  | { event: "record_unsaved"; properties: { masterId: string; listType: "want" | "collection" } }
  | { event: "platform_connected"; properties: { platform: "spotify" | "lastfm" | "discogs" } }
  | { event: "platform_disconnected"; properties: { platform: "spotify" | "lastfm" | "discogs" } }
  | { event: "onboarding_completed"; properties?: Record<string, unknown> };

export function track<E extends AnalyticsEvent["event"]>(
  event: E,
  properties: Extract<AnalyticsEvent, { event: E }>["properties"],
) {
  if (!initialized) return;
  posthog.capture(event, properties);
}
